import { collection, getDocs, getFirestore, query } from "firebase/firestore";
import { CustomClaims, KfhTradeAccount, Portfolio, Position, PositionDetails, StockBalance } from "../types";
const db = getFirestore();

/** Who may see KFH Trade account balances in the CRM (matches customer positions UI). */
export const canViewKfhTradeAccounts = (userClaims?: CustomClaims | null): boolean =>
    !!(userClaims?.admin || userClaims?.cr);


export const getCustomerStoredAccounts = async (customerID: any) => {

    if (customerID) {


        const customerRef = collection(
            db,
            "customers",
            customerID,
            "accounts"
        )
        const accountsQuery = query(customerRef);
        const accountsData = await getDocs(accountsQuery);
        return accountsData.docs;
    }
    return []
}

export const getCustomerStoredPortfolios = async (customerID: any) => {

    const customerRef = collection(
        db,
        "customers",
        customerID,
        "portfolios"
    )
    const portfoliosQuery = query(customerRef);
    const portfoliosData = await getDocs(portfoliosQuery);
    return portfoliosData.docs;
}

export const convertKfhTradeAccountsToPortfolio = (kfhTradeAccounts: KfhTradeAccount[], rates: Record<string, string>): Portfolio => {
    if (!kfhTradeAccounts?.length) {
        return {
            code: '',
            crmID: '',
            name: '',
            investmentPortfolio: '',
            value: '',
            position: {
                managed_by: 'customer',
                status: 'Active',
                cash: 0,
                securities: 0,
                total_capital_market_value: 0,
                cash_percent: 0,
                unrealized_profit_loss: 0,
                local_position: [],
                foreign_position: [],
            },
        };
    }

    // Pick a representative row for IDs/names
    const first = kfhTradeAccounts[0];
    const portfolioCode = `KFHTrade-${first.KFHTRADE_ID ?? ''}`;
    const crmID = first.CRM_ID ?? '';
    const portfolioName = first.ACCOUNT_NAME ?? '';

    // Helpers
    const toNumber = (n: unknown) =>
        typeof n === 'number' && Number.isFinite(n) ? n : undefined;

    const getRate = (ccy: string): number | undefined => {
        //convert string rate to number
        const rate = ccy === 'KWD' ? 1 : rates?.[ccy];
        if (typeof rate === 'string') {
            const parsed = parseFloat(rate);
            return Number.isFinite(parsed) ? parsed : undefined;
        }
        return rate;
    };

    const toKWD = (
        amount: number | undefined,
        ccy: string
    ): number | undefined => {
        if (amount === undefined) return undefined;
        const r = getRate(ccy);
        if (r === undefined) return undefined;
        return amount * r;
    };

    const local_position: Position[] = [];
    const foreign_position: Position[] = [];

    let totalCashKWD = 0;
    let totalSecsKWD = 0;
    let unrealizedTotalKWD = 0;

    // CASH rows → one "Cash" position per currency occurrence
    for (const row of kfhTradeAccounts.filter((r) => r.SOURCE === 'CASH')) {
        if (row.cash_balance === undefined) {
            continue;
        }
        const ccy = row.CURRENCY;
        const balance = toNumber(row.cash_balance?.balance);
        const mvKWD = toKWD(balance, ccy);

        const pos: Position = {
            firebase_key: `cash:${portfolioCode}:${ccy}`,
            name: 'Cash',
            cash: true,
            currency: ccy,
            code: ccy,
            exchange: ccy,
            total_market_value: balance,
            quantity: balance,
            total_market_value_local: mvKWD,
        };

        if (ccy === 'KWD') local_position.push(pos);
        else foreign_position.push(pos);

        if (mvKWD !== undefined) totalCashKWD += mvKWD;
    }

    // SECURITIES rows → one position per stock line
    for (const row of kfhTradeAccounts.filter((r) => r.SOURCE === 'SECURITY')) {
        const ccy = row.CURRENCY;
        const stocks: StockBalance[] = row.stock_balance ?? [];
        for (const s of stocks) {
            const qty = toNumber(s.netHoldings) ?? 0;
            const ltp = toNumber(s.lastTradePrice) ?? 0;

            const averageCost =
                toNumber(s.avgCost) ??
                toNumber(s.previousClosed);

            const mvNative = qty * ltp;
            const tcNative = qty * (averageCost ?? 0);
            const mvKWD = toKWD(mvNative, ccy);

            const unrealizedNative =
                averageCost !== undefined ? (ltp - averageCost) * qty : undefined;
            const unrealizedKWD = toKWD(unrealizedNative, ccy);

            const pos: Position = {
                firebase_key: `sec:${portfolioCode}:${s.symbol}`,
                ticker: s.symbol,
                name: s.symbol,
                exchange: s.exchange,
                currency: ccy,
                quantity: qty,
                average_cost: averageCost,
                total_cost: tcNative,
                total_market_value: mvNative,
                total_market_value_local: mvKWD,
                unrealized_gain_loss: unrealizedNative,
                total_unrealized_gain_loss_local: unrealizedKWD,
                gain_loss_percent:
                    averageCost && averageCost !== 0
                        ? ((ltp - averageCost) / averageCost) * 100
                        : undefined,
            };

            if (ccy === 'KWD') local_position.push(pos);
            else foreign_position.push(pos);

            if (mvKWD !== undefined) totalSecsKWD += mvKWD;
            if (unrealizedKWD !== undefined) unrealizedTotalKWD += unrealizedKWD;
        }
    }

    const total_capital_market_value = totalCashKWD + totalSecsKWD;
    const cash_percent =
        total_capital_market_value > 0
            ? (totalCashKWD / total_capital_market_value) * 100
            : 0;

    const position: PositionDetails = {
        // required defaults
        managed_by: 'customer',
        status: 'Active',

        // computed
        local_position: local_position.length ? local_position : undefined,
        foreign_position: foreign_position.length ? foreign_position : undefined,
        cash: totalCashKWD,
        securities: totalSecsKWD,
        total_capital_market_value,
        cash_percent,
        unrealized_profit_loss: unrealizedTotalKWD,

    };

    const portfolio: Portfolio = {
        code: portfolioCode,                  // = KFHTRADE_ID (from first row)
        status: 'Active',
        name: portfolioName || '',
        crmID: crmID || '',
        position,
        value: ''
    };

    return portfolio;
}

export const hasKFHTradeBalance = (kfhTradeAccounts: KfhTradeAccount[]): boolean => {
    return kfhTradeAccounts.some(account => {
        const hasCash =
            account.cash_balance &&
            Object.values(account.cash_balance).some(v => !!v);

        const hasStock =
            account.stock_balance &&
            Object.values(account.stock_balance).some(v => !!v);

        return hasCash || hasStock
    })
}